import { debugLogger } from '@/utils/debugLogger';

export type PhotoVersion = 'v0' | 'v0.5' | 'v1' | 'v1.5';

export interface PhotoVersionInfo {
  version: PhotoVersion;
  hasVariants: boolean;
  variantsSuffix: string[];
  isHashedPath: boolean;
}

/**
 * Detecta a versão da foto baseado na URL
 */
export const detectPhotoVersion = (url: string): PhotoVersionInfo => {
  if (!url) {
    return { version: 'v0', hasVariants: false, variantsSuffix: [], isHashedPath: false };
  }

  // v1.5: NOVO - Privado + hash path 16 chars + hash filename + variantes numéricas underscore
  const isSignedUrl = url.includes('/object/sign/');
  const hasHashPath = /profile_photos\/[a-f0-9]{16}\//.test(url);
  const hasHashFilename = /\/[a-f0-9]{16}_[0-3]\.jpg/.test(url);
  const hasUnderscoreVariant = /_[0-3]\.jpg/.test(url);
  
  if (isSignedUrl && hasHashPath && hasHashFilename && hasUnderscoreVariant) {
    debugLogger.debug('[photoVersionDetector] Detected v1.5 photo (signed URL)', {
      url: url.substring(0, 100)
    });
    return {
      version: 'v1.5',
      hasVariants: true,
      variantsSuffix: ['_0.jpg', '_1.jpg', '_2.jpg', '_3.jpg'],
      isHashedPath: true
    };
  }

  // v1.5 path relativo (sem signed URL) - formato: userHash/imageHash_variant.jpg
  const hasRelativeV15Path = /^[a-f0-9]{16}\/[a-f0-9]{16}_[0-3]\.jpg$/.test(url);
  if (hasRelativeV15Path) {
    debugLogger.debug('[photoVersionDetector] Detected v1.5 photo (relative path)', {
      url: url.substring(0, 100)
    });
    return {
      version: 'v1.5',
      hasVariants: true,
      variantsSuffix: ['_0.jpg', '_1.jpg', '_2.jpg', '_3.jpg'],
      isHashedPath: true
    };
  }

  // v1: Hash 16 chars + público + variantes semânticas antigas OU sem variantes
  const v1Variants = ['-micro-avatar.jpg', '-small-thumb.jpg', '-medium-card.jpg', '-large-display.jpg'];
  const hasV1Variant = v1Variants.some(variant => url.includes(variant));

  if (hasV1Variant || (hasHashPath && !hasUnderscoreVariant)) {
    return {
      version: 'v1',
      hasVariants: hasV1Variant,
      variantsSuffix: hasV1Variant ? v1Variants : [],
      isHashedPath: hasHashPath
    };
  }

  // v0.5: Sistema antigo com 3 variantes
  const v05Variants = ['-ultra-thumb.jpg', '-medium-display.jpg', '-full-quality.jpg'];
  const hasV05Variant = v05Variants.some(variant => url.includes(variant));

  if (hasV05Variant) {
    return {
      version: 'v0.5',
      hasVariants: true,
      variantsSuffix: v05Variants,
      isHashedPath: false
    };
  }

  // v0: Sistema antigo sem variantes
  return {
    version: 'v0',
    hasVariants: false,
    variantsSuffix: [],
    isHashedPath: false
  };
};

/**
 * Remove sufixo de variante da URL para obter URL base
 */
export const getBaseUrl = (url: string): string => {
  return url
    // v1.5 variants (underscore)
    .replace(/_0\.jpg$/, '.jpg')
    .replace(/_1\.jpg$/, '.jpg')
    .replace(/_2\.jpg$/, '.jpg')
    .replace(/_3\.jpg$/, '.jpg')
    // v1 variants (hífen semântico)
    .replace(/-micro-avatar\.jpg$/, '.jpg')
    .replace(/-small-thumb\.jpg$/, '.jpg')
    .replace(/-medium-card\.jpg$/, '.jpg')
    .replace(/-large-display\.jpg$/, '.jpg')
    // v0.5 variants
    .replace(/-ultra-thumb\.jpg$/, '.jpg')
    .replace(/-medium-display\.jpg$/, '.jpg')
    .replace(/-full-quality\.jpg$/, '.jpg');
};

/**
 * Gera todas as possíveis URLs de variantes para testar
 */
export const generatePossibleVariantUrls = (baseUrl: string): string[] => {
  const cleanUrl = getBaseUrl(baseUrl);
  const basePath = cleanUrl.replace(/\.jpg$/, '');

  return [
    // v1 (prioridade)
    `${basePath}-large-display.jpg`,
    `${basePath}-medium-card.jpg`,
    `${basePath}-small-thumb.jpg`,
    `${basePath}-micro-avatar.jpg`,
    // v0.5 (fallback)
    `${basePath}-full-quality.jpg`,
    `${basePath}-medium-display.jpg`,
    `${basePath}-ultra-thumb.jpg`,
    // v0 (original)
    cleanUrl
  ];
};

/**
 * Testa qual URL existe fazendo HEAD requests
 */
export const findExistingPhotos = async (possibleUrls: string[]): Promise<Map<string, number>> => {
  const existingPhotos = new Map<string, number>();

  debugLogger.debug('[photoVersionDetector] Testing URLs:', { count: possibleUrls.length });

  const results = await Promise.allSettled(
    possibleUrls.map(async (url) => {
      try {
        const response = await fetch(url, { method: 'HEAD' });
        if (response.ok) {
          const size = parseInt(response.headers.get('content-length') || '0');
          return { url, size };
        }
        return null;
      } catch (error) {
        return null;
      }
    })
  );

  results.forEach((result) => {
    if (result.status === 'fulfilled' && result.value) {
      existingPhotos.set(result.value.url, result.value.size);
    }
  });

  debugLogger.debug('[photoVersionDetector] Found existing photos:', {
    count: existingPhotos.size,
    photos: Array.from(existingPhotos.entries()).map(([url, size]) => ({ url, size }))
  });

  return existingPhotos;
};

/**
 * Encontra a melhor foto fonte (maior tamanho = melhor qualidade)
 */
export const findBestSourcePhoto = (existingPhotos: Map<string, number>): { url: string; size: number } | null => {
  if (existingPhotos.size === 0) return null;

  let bestPhoto: { url: string; size: number } | null = null;

  existingPhotos.forEach((size, url) => {
    if (!bestPhoto || size > bestPhoto.size) {
      bestPhoto = { url, size };
    }
  });

  debugLogger.info('[photoVersionDetector] Best source photo:', bestPhoto);
  return bestPhoto;
};
